// The AI Champions program: its six communities and the champions in each,
// from the AI Champions Roster (11.2.26). The live data is in the Champion
// lists in SharePoint (see api/champions.ts); this is what the page falls back
// to, and where the photos bundled in /public/champions are matched by name.

export type CommunityId = "land" | "finance" | "construction" | "sales" | "tech" | "corporate";

export type Community = {
  id: CommunityId;
  /** Its row in the Champion Communities list, once loaded from SharePoint. */
  itemId?: string;
  name: string;
  /** What AI could look like in this community's day-to-day work. */
  examples: string[];
};

export type Champion = {
  /** Its row in the Champion Roster list, once loaded from SharePoint. */
  id?: string;
  name: string;
  community: CommunityId;
  /** The part of the business they come from, as listed on the roster. */
  area: string;
  title?: string;
  location?: string;
  email?: string;
  /** A link to their photo (from SharePoint), or a photo in /public/champions;
   *  otherwise their Microsoft 365 photo is used. */
  photo?: string;
};

export const COMMUNITIES: Community[] = [
  {
    id: "land",
    name: "Land Entitlements & Development",
    examples: [
      "First-pass review of planning, zoning, density and comparables during early screening",
      "Risk summaries of contracts and PSAs before detailed review",
      "Quick answers on contract, bonding and onboarding questions",
    ],
  },
  {
    id: "finance",
    name: "Finance, Accounting & Investments",
    examples: [
      "Drafting first versions of IC memos and capital partner updates",
      "Summarizing NDAs and other documents before detailed review",
      "Speeding up Excel formulas, reporting and pro-forma analysis",
    ],
  },
  {
    id: "construction",
    name: "Construction, Operations & Supply Chain",
    examples: [
      "Meeting recaps and action items right after the meeting ends",
      "Side-by-side comparisons of bids and quotes",
      "Summarizing long documents, specs and reports",
    ],
  },
  {
    id: "sales",
    name: "Sales & Marketing",
    examples: [
      "Drafting content briefs, social copy and website content",
      "Summarizing competitor pricing, incentives and product offerings",
      "Checking how our communities show up in AI search answers",
    ],
  },
  {
    id: "tech",
    name: "Technology, Data & Automation",
    examples: [
      "Turning repetitive requests into reusable prompts, SOPs or automations",
      "Documenting workflows so other regions can pick them up",
      "Getting faster, clearer answers from our reporting data",
    ],
  },
  {
    id: "corporate",
    name: "Corporate Services, Legal & Risk",
    examples: [
      "First-pass summaries of contracts, agreements and policies",
      "Drafting and tightening correspondence, memos and FAQs",
      "Clear answers on what's safe to use AI for with sensitive information",
    ],
  },
];

export const COMMUNITY_BY_ID = Object.fromEntries(COMMUNITIES.map((c) => [c.id, c])) as Record<CommunityId, Community>;

const RP = "brookfieldrp.com";
const BPD = "brookfieldpropertiesdevelopment.com";

export const CHAMPIONS: Champion[] = [
  // Land Entitlements & Development
  { name: "Afrika Tesfa", community: "land", area: "Development / Finance", title: "Development Coordinator", location: "Federal Way, WA", email: `Afrika.Tesfa@${RP}` },
  { name: "Colin Koch", community: "land", area: "Land", title: "Sr. Director, Land", location: "Costa Mesa, CA", email: `Colin.Koch@${RP}`, photo: "colinkoch" },
  { name: "Anudeep Janda", community: "land", area: "Forward Planning", title: "Project Manager", location: "San Ramon, CA", email: `Anudeep.Janda@${RP}`, photo: "anudeepjanda" },
  { name: "David Greenstein", community: "land", area: "Land & Planning", title: "Director, Land Development", location: "San Ramon, CA", email: `David.Greenstein@${RP}`, photo: "davidgreenstein" },
  { name: "Jonathan Finkbeiner", community: "land", area: "Land Development", title: "Land Contracts Coordinator", location: "Reston, VA", email: `Jonathan.Finkbeiner@${RP}`, photo: "jonathanfinkbeiner" },
  { name: "Scott Anderson", community: "land", area: "Land Development", title: "Development Manager", location: "Federal Way, WA", email: `Scott.Anderson@${RP}` },
  { name: "Sulaiman Wara", community: "land", area: "Land Development", title: "Financial Analyst", location: "Houston, TX", email: `Sulaiman.Wara@${BPD}`, photo: "sulaimanwara" },
  { name: "Jason Nay", community: "land", area: "Land", title: "Regional Director, Land Operations", location: "Costa Mesa, CA", email: `Jason.Nay@${RP}` },
  { name: "Niko Waldon", community: "land", area: "Land, Development & Entitlements", title: "Project Manager", location: "Houston, TX", email: `Niko.Waldon@${BPD}`, photo: "nikowaldon" },
  { name: "Kristal Orni", community: "land", area: "Development & Entitlements", email: `Kristal.Orni@${RP}` },
  // Finance, Accounting & Investments
  { name: "Connor Cole", community: "finance", area: "Investments / Land Acquisition", title: "Real Estate Analyst", location: "Austin, TX", email: `Connor.Cole@${RP}` },
  { name: "Darren Kogut", community: "finance", area: "Accounting", title: "Assistant Controller", location: "Centennial, CO", email: `Darren.Kogut@${RP}`, photo: "darrenkogut" },
  // Construction, Operations & Supply Chain
  { name: "David Bartos", community: "construction", area: "Construction", title: "Area Construction Manager", location: "Reston, VA", email: `David.Bartos@${RP}`, photo: "davidbartos" },
  { name: "Christina Rego", community: "construction", area: "Customer Care", title: "Director, Customer Care & CX", location: "Toronto, ON", email: `Christina.Rego@${RP}`, photo: "christinarego" },
  // Sales & Marketing
  { name: "Cassie Cataline", community: "sales", area: "Marketing", title: "Marketing Director, Nexton", location: "Summerville, SC", email: `Cassie.Cataline@${RP}`, photo: "cassiecataline" },
  { name: "Jessica McDonnell", community: "sales", area: "Marketing", email: `Jessica.McDonnell@${RP}` },
  { name: "Luciano Ramirez-Reyes", community: "sales", area: "Sales", title: "Real Estate Analyst", location: "Calgary, AB", email: `Luciano.Reyes@${RP}` },
  { name: "Ron Slater", community: "sales", area: "Sales", title: "Area Sales Manager", location: "Houston, TX", email: `Ron.Slater@${RP}`, photo: "ronslater" },
  { name: "Avrie Stoll", community: "sales", area: "Marketing", email: `Avrie.Stoll@${RP}` },
  // Technology, Data & Automation
  { name: "Karlee Anton", community: "tech", area: "IT", title: "Sr. IT Project Manager", location: "Calgary, AB", email: `Karlee.Anton@${RP}`, photo: "karleeanton" },
  { name: "Suhi Siva", community: "tech", area: "IT", title: "Applications Architect", location: "Toronto, ON", email: `Suhi.Siva@${RP}`, photo: "suhisiva" },
  { name: "Brennan Deschambault", community: "tech", area: "IT", title: "Vice President, Technology", location: "Calgary, AB", email: `Brennan.Deschambault@${RP}`, photo: "brennandeschambault" },
  { name: "Jordan Petty", community: "tech", area: "IT", title: "Sr. BI Analyst", location: "Houston, TX", email: `Jordan.Petty@${RP}`, photo: "jordanpetty" },
  { name: "Jason Gray", community: "tech", area: "IT", title: "Sr. Manager, Land Applications", location: "Calgary, AB", email: `Jason.Gray@${RP}`, photo: "jasongray" },
  // Corporate Services, Legal & Risk
  { name: "Mary Catherine Wells", community: "corporate", area: "Human Resources", title: "Advisor, Human Resources", location: "Calgary, AB", email: `MaryCatherine.Wells@${RP}`, photo: "marycatherinewells" },
  { name: "Jake Riter", community: "corporate", area: "Risk Management", title: "Risk Management Administrator", location: "Costa Mesa, CA", email: `Jake.Riter@${RP}`, photo: "jakeriter" },
];

/** Program milestones, on every community's timeline until set per community. */
/** A date on a community's AI Champions timeline. No community means it's
 *  on every community's timeline. */
export type Milestone = { id?: string; date: string; text: string; community?: CommunityId };

export const MILESTONES: Milestone[] = [
  { date: "2026-11-06", text: "Outreach email and AI Journey survey go out" },
  { date: "2026-11-17", text: "November AI Update introduces the Champions" },
  { date: "2026-11-20", text: "Champions review survey results together" },
  { date: "2026-12-04", text: "First AI office hours in every community" },
  { date: "2026-12-18", text: "Use cases and process ideas submitted" },
];

/** People to talk to about joining the program. */
export const PROGRAM_LEADS = ["Colin Koch", "Cassie Cataline", "Afrika Tesfa"];

export const SURVEY_URL = "https://forms.cloud.microsoft/r/FUq7EiMWPV";

/** Everything an admin can change about the Champions page. Loaded from the
 *  Champion lists in SharePoint (see api/champions.ts); the values here are the
 *  fallback when SharePoint can't be reached. */
export type ChampionsConfig = {
  headline: string;
  lede: string;
  surveyUrl: string;
  /** Left out of Top contributors and the Champions Cup, by email or name. */
  leaderboardExclude: string[];
  /** Names of the people to contact about joining (up to three). */
  leads: string[];
  communities: Community[];
  champions: Champion[];
  milestones: Milestone[];
};

export const DEFAULT_CONFIG: ChampionsConfig = {
  headline: "Meet the people helping us work smarter with AI",
  lede: "Colleagues in every part of the business who help their teams discover, adopt and share practical AI. Find your community and say hi.",
  surveyUrl: SURVEY_URL,
  leaderboardExclude: [],
  leads: PROGRAM_LEADS,
  communities: COMMUNITIES,
  champions: CHAMPIONS,
  milestones: MILESTONES,
};

/** The community a department most likely belongs to, for pointing people at
 *  their champions. First match wins. */
const DEPARTMENT_RULES: [RegExp, CommunityId][] = [
  [/\b(it|information technology|technology|data|digital|automation|systems|applications?)\b/i, "tech"],
  [/financ|account|invest|controller|treasury|tax|fp&a/i, "finance"],
  [/sales|market|brand|digital marketing|interior design/i, "sales"],
  [/construct|operation|supply|purchas|procure|customer care|warranty|build/i, "construction"],
  [/land|develop|entitle|planning|acquisition/i, "land"],
  [/human|\bhr\b|people|legal|risk|corporate|executive|office|admin/i, "corporate"],
];

export function communityForDepartment(department?: string | null): CommunityId | null {
  if (!department) return null;
  return DEPARTMENT_RULES.find(([re]) => re.test(department))?.[1] ?? null;
}
